import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/hooks/useProducts';
import { useProductTranslation } from '@/hooks/useProductTranslation';

interface RelatedProductsProps {
  currentId: string;
  category: string;
}

const TAG_I18N_MAP: Record<string, string> = {
  '热销中': 'tag_hot_selling',
  '新品': 'tag_new',
  '瓦楞纸': 'tag_corrugated_paper',
  '限时特惠': 'tag_limited_offer',
  '爆款': 'tag_bestseller',
  '推荐': 'tag_recommended',
  '猫薄荷': 'tag_catnip',
  '剑麻': 'tag_sisal',
  '护角': 'tag_corner_protector',
  '爬架': 'tag_cat_tree',
  '环保': 'tag_eco_friendly',
};

const CATEGORY_I18N_MAP: Record<string, string> = {
  '瓦楞纸': 'category_corrugated',
  '剑麻': 'category_sisal',
  '综合': 'category_combo',
};

function RelatedCard({ product }: { product: any }) {
  const { t } = useTranslation();
  const { getProductName } = useProductTranslation();
  const displayName = getProductName(product);

  const translateTag = (tag: string): string => {
    const key = TAG_I18N_MAP[tag];
    return key ? t(key) : tag;
  };

  const translateCategory = (category: string): string => {
    const key = CATEGORY_I18N_MAP[category];
    return key ? t(key) : category;
  };

  return (
    <Link
      to={`/products/${product.id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-[#f0ebe4] hover:border-[#C8714A]/40 transition-all duration-300 cursor-pointer"
    >
      <div className="relative overflow-hidden bg-[#faf8f5] h-44">
        <img
          src={product.image}
          alt={displayName}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
          {product.tags?.slice(0, 1).map((tag: string) => (
            <span key={tag} className="px-2 py-0.5 rounded-full bg-[#C8714A] text-white text-xs font-medium">
              {translateTag(tag)}
            </span>
          ))}
        </div>
      </div>
      <div className="p-4">
        <div className="text-xs text-[#C8714A] mb-1">{translateCategory(product.category)}</div>
        <h3 className="text-sm font-semibold text-[#1a1a1a] mb-2 group-hover:text-[#C8714A] transition-colors">
          {displayName}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-[#C8714A]">${product.price}</span>
          {product.original_price > product.price && (
            <span className="text-xs text-[#ccc] line-through">${product.original_price}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function RelatedProducts({ currentId, category }: RelatedProductsProps) {
  const { t } = useTranslation();
  const { products, loading } = useProducts({ category });

  const related = products.filter((p) => p.id !== currentId).slice(0, 3);

  if (loading) {
    return (
      <section className="mt-16 pt-12 border-t border-[#f0ebe4]">
        <h2 className="text-xl font-bold text-[#1a1a1a] mb-8">{t('related_title')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden border border-[#f0ebe4] animate-pulse">
              <div className="h-44 bg-[#f5f0eb]"></div>
              <div className="p-4 flex flex-col gap-2">
                <div className="h-3 w-12 bg-[#f0ebe4] rounded-full"></div>
                <div className="h-4 w-3/4 bg-[#f0ebe4] rounded"></div>
                <div className="h-5 w-16 bg-[#f0ebe4] rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (related.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-[#f0ebe4]">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-bold text-[#1a1a1a]">{t('related_title')}</h2>
        <Link
          to="/products"
          className="text-sm text-[#C8714A] hover:text-[#b5603b] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
        >
          {t('related_view_all')}
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-product-shop>
        {related.map((product) => (
          <RelatedCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  );
}
